import { AccountType, EffectiveSubscriptionState, Permission, SubscriptionStatus } from '@ee/common/enums';

/**
 * Effective subscription states in which an attorney org retains full access to the portal.
 *
 * Single source of truth: this list used to be copy-pasted into the route guard, the user resolver
 * and the auth effect, and the three copies were free to drift.
 */
export const SUBSCRIPTION_ACTIVE_STATES: EffectiveSubscriptionState[] = [
  EffectiveSubscriptionState.ACTIVE,
  EffectiveSubscriptionState.TRIAL,
  EffectiveSubscriptionState.FREE,
  EffectiveSubscriptionState.GRACE_PERIOD
];

/**
 * The permissions that let a user reach the billing screens (`/plan`, `/admin/billing`) and fix a
 * lapsed subscription. Note the built-in attorney "Admin" permission group holds NEITHER — only
 * "Account Admin" does (see PermissionGroupUtils.generateAttorneyAdminGroup on the backend).
 */
export const BILLING_PERMISSIONS: string[] = [Permission.ADMIN_BILLING_VIEW, Permission.ADMIN_BILLING_EDIT];

/**
 * The org types that pay for a subscription and so are gated on it: attorneys, and, since EE-500,
 * property managers. Client orgs ride on their attorney's account and are never gated.
 */
function isSubscribingOrg(org: { type?: AccountType } | null | undefined): boolean {
  return org?.type === AccountType.ATTORNEY || org?.type === AccountType.PROPERTY_MANAGER;
}

/**
 * Whether the org's subscription still grants access. The gate covers attorney and property-manager
 * orgs (EE-500); client orgs are never blocked on it, so they always read as active here.
 *
 * A property-manager org that never subscribed has no status, which the server maps to EXPIRED: it
 * reads as inactive here on purpose, because choosing a plan is the next step for it.
 *
 * Accepts any object with the two fields it reads, to avoid a circular dependency between
 * @ee/common/utils and @ee/common/models.
 */
export function isSubscriptionActive(
  org: { type?: AccountType; effective_subscription_state?: EffectiveSubscriptionState } | null | undefined
): boolean {
  if (!isSubscribingOrg(org)) {
    return true;
  }
  return SUBSCRIPTION_ACTIVE_STATES.includes(org.effective_subscription_state);
}

/**
 * Whether the org is a trial that has expired, and so is read-only rather than locked out (EE-444 for
 * attorneys, EE-500 for property managers). The server refuses its writes with 402; the portal still
 * lets it see everything.
 *
 * Reads `status`, not `effective_subscription_state`: both a trial expiry and a cancellation surface as
 * EXPIRED there, and only the trial is read-only.
 */
export function isTrialExpiredReadOnly(
  org: { type?: AccountType; status?: SubscriptionStatus } | null | undefined
): boolean {
  return isSubscribingOrg(org) && org.status === SubscriptionStatus.SUBSCRIPTION_TRIAL_EXPIRED;
}

/**
 * A property-manager org that has never started a subscription (EE-500): no status at all. It gets a
 * "choose a plan to start your free trial" prompt rather than an "expired" one.
 */
export function isPropertyManagerWithoutPlan(
  org: { type?: AccountType; status?: SubscriptionStatus } | null | undefined
): boolean {
  return org?.type === AccountType.PROPERTY_MANAGER && !org.status;
}

/**
 * Whether the user may act on a subscription problem — i.e. whether pointing them at the billing
 * screens is useful rather than a dead end.
 *
 * VIEW *or* EDIT, deliberately: `/plan` declares ADMIN_BILLING_EDIT while `/admin/billing` declares
 * ADMIN_BILLING_VIEW, so gating the escape hatch on only one of them strands holders of the other.
 */
export function canManageBilling(user: { permissions?: string[] } | null | undefined): boolean {
  return user?.permissions?.some(permission => BILLING_PERMISSIONS.includes(permission)) || false;
}

/**
 * Whether the user can open `/admin/billing` itself. Its route guard asks for ADMIN_BILLING_VIEW only,
 * so a link there must be gated on that one permission: `canManageBilling` (VIEW *or* EDIT) is right
 * for "is there anything this user can do about billing", but an EDIT-only holder sent to the billing
 * page is bounced straight back by the guard.
 */
export function canOpenBillingPage(user: { permissions?: string[] } | null | undefined): boolean {
  return user?.permissions?.includes(Permission.ADMIN_BILLING_VIEW) || false;
}

const DAY_MS = 1000 * 60 * 60 * 24;

/** Midnight at the start of the local calendar day containing `ms`. */
function startOfLocalDay(ms: number): number {
  const day = new Date(ms);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

/**
 * The top menu's trial countdown (EE-445). Counts local calendar days, so "today" and "tomorrow" agree
 * with the date shown next to it: a duration rounded up to whole days called a trial ending this
 * afternoon "tomorrow" and one ending 25 hours from now "in 2 days". Empty for an unparseable date.
 */
export function trialCountdownText(trialEndDate: string, now: number = Date.now()): string {
  const end = Date.parse(trialEndDate);
  if (isNaN(end)) {
    return '';
  }
  // Math.round, not a division: a DST change makes one of the days 23 or 25 hours long.
  const days = Math.round((startOfLocalDay(end) - startOfLocalDay(now)) / DAY_MS);
  if (days < 0) {
    // The status can lag the real end (a missed webhook, a sync not yet run); say so rather than
    // repeating "ends today" every day until it catches up.
    return `Your trial ended ${new Date(end).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}.`;
  }
  if (days === 0) {
    return 'Your trial ends today.';
  }
  return days === 1 ? 'Your trial ends tomorrow.' : `Your trial ends in ${days} days.`;
}
